// ============================================
// LAYOUT DEFINITIONS
// ============================================
// All layouts use slot-based system for precise component placement.
// Each layout defines:
// - Named slots/zones for component placement
// - Component suggestions per slot
// - Visual placeholders in builder
// - Responsive grid positioning
// - Drag-drop support with multiple components per slot
// ============================================

export const LAYOUTS = [
  // ============================================
  // SINGLE COLUMN LAYOUT
  // ============================================
  { 
    id: 'single-column', 
    name: 'Single Column',
    description: 'Full-width single column layout - perfect for blogs, articles, and focused content',
    preview: '/assets/layout-icons/single-column.png',
    gridClass: 'single-column-grid',
    type: 'slot-based',
    layoutSpacing: {
      desktop: { gap: 28, padding: 20 },
      tablet: { gap: 24, padding: 16 },
      mobile: { gap: 16, padding: 12 }
    },
    slots: [
      {
        id: 'header',
        name: 'Header',
        position: { row: 1, col: 1 },
        suggestedComponents: ['header', 'image', 'carousel'],
        gridArea: 'header',
        placeholder: 'Add header content',
        allowMultiple: true,
        maxComponents: 3
      },
      {
        id: 'content',
        name: 'Main Content',
        position: { row: 2, col: 1 },
        suggestedComponents: ['text', 'paragraph', 'card', 'image', 'form', 'table', 'list'],
        gridArea: 'content',
        placeholder: 'Add your main content here',
        allowMultiple: true,
        maxComponents: 20,
        required: true
      },
      {
        id: 'footer',
        name: 'Footer',
        position: { row: 3, col: 1 },
        suggestedComponents: ['text', 'paragraph', 'button', 'list'],
        gridArea: 'footer',
        placeholder: 'Add footer content',
        allowMultiple: true,
        maxComponents: 5
      }
    ]
  },

  // ============================================
  // TWO COLUMN LAYOUT
  // ============================================
  { 
    id: 'two-column', 
    name: 'Two Column Split',
    description: 'Equal split two-column layout - ideal for comparing content or side-by-side displays',
    preview: '/assets/layout-icons/two-column.png',
    gridClass: 'two-column-grid',
    type: 'slot-based',
    layoutSpacing: {
      desktop: { gap: 24, padding: 20 },
      tablet: { gap: 20, padding: 16 },
      mobile: { gap: 16, padding: 12 }
    },
    slots: [
      {
        id: 'header',
        name: 'Header',
        position: { row: 1, col: '1/3' },
        suggestedComponents: ['header', 'image', 'text'],
        gridArea: 'header',
        placeholder: 'Add header spanning both columns',
        allowMultiple: true,
        maxComponents: 3
      },
      {
        id: 'left-column',
        name: 'Left Column',
        position: { row: 2, col: 1 },
        suggestedComponents: ['card', 'image', 'text', 'form', 'list'],
        gridArea: 'left',
        placeholder: 'Add left column content',
        allowMultiple: true,
        maxComponents: 10,
        required: true
      },
      {
        id: 'right-column',
        name: 'Right Column',
        position: { row: 2, col: 2 },
        suggestedComponents: ['card', 'image', 'text', 'form', 'list'],
        gridArea: 'right',
        placeholder: 'Add right column content',
        allowMultiple: true,
        maxComponents: 10,
        required: true
      },
      {
        id: 'footer',
        name: 'Footer',
        position: { row: 3, col: '1/3' },
        suggestedComponents: ['text', 'paragraph', 'button'],
        gridArea: 'footer',
        placeholder: 'Add footer content',
        allowMultiple: true,
        maxComponents: 5
      }
    ]
  },

  // ============================================
  // THREE COLUMN LAYOUT
  // ============================================
  { 
    id: 'three-column', 
    name: 'Three Equal Columns',
    description: 'Three equal columns - great for features, services, or product showcases',
    preview: '/assets/layout-icons/three-column.png',
    gridClass: 'three-column-grid',
    type: 'slot-based',
    layoutSpacing: {
      desktop: { gap: 24, padding: 20 },
      tablet: { gap: 20, padding: 16 },
      mobile: { gap: 16, padding: 12 }
    },
    slots: [
      {
        id: 'header',
        name: 'Header',
        position: { row: 1, col: '1/4' },
        suggestedComponents: ['header', 'image', 'text'],
        gridArea: 'header',
        placeholder: 'Add header spanning all columns',
        allowMultiple: true,
        maxComponents: 3
      },
      {
        id: 'column-1',
        name: 'Column 1',
        position: { row: 2, col: 1 },
        suggestedComponents: ['card', 'image', 'text', 'button'],
        gridArea: 'col1',
        placeholder: 'Add first column content',
        allowMultiple: true,
        maxComponents: 5,
        required: true
      },
      {
        id: 'column-2',
        name: 'Column 2',
        position: { row: 2, col: 2 },
        suggestedComponents: ['card', 'image', 'text', 'button'],
        gridArea: 'col2',
        placeholder: 'Add second column content',
        allowMultiple: true,
        maxComponents: 5,
        required: true
      },
      {
        id: 'column-3',
        name: 'Column 3',
        position: { row: 2, col: 3 },
        suggestedComponents: ['card', 'image', 'text', 'button'],
        gridArea: 'col3',
        placeholder: 'Add third column content',
        allowMultiple: true,
        maxComponents: 5,
        required: true
      },
      {
        id: 'footer',
        name: 'Footer',
        position: { row: 3, col: '1/4' },
        suggestedComponents: ['text', 'paragraph', 'button'],
        gridArea: 'footer',
        placeholder: 'Add footer content',
        allowMultiple: true,
        maxComponents: 5
      }
    ]
  },

  // ============================================
  // FOUR COLUMN LAYOUT
  // ============================================
  { 
    id: 'four-column', 
    name: 'Four Equal Columns',
    description: 'Four equal columns - perfect for galleries, pricing tables, or team showcases',
    preview: '/assets/layout-icons/four-equal-columns.png',
    gridClass: 'four-column-grid',
    type: 'slot-based',
    layoutSpacing: {
      desktop: { gap: 20, padding: 20 },
      tablet: { gap: 16, padding: 16 },
      mobile: { gap: 12, padding: 12 }
    },
    slots: [
      {
        id: 'header',
        name: 'Header',
        position: { row: 1, col: '1/5' },
        suggestedComponents: ['header', 'image', 'text'],
        gridArea: 'header',
        placeholder: 'Add header spanning all columns',
        allowMultiple: true,
        maxComponents: 3
      },
      {
        id: 'column-1',
        name: 'Column 1',
        position: { row: 2, col: 1 },
        suggestedComponents: ['card', 'image', 'text', 'button'],
        gridArea: 'col1',
        placeholder: 'Add first column',
        allowMultiple: true,
        maxComponents: 5,
        required: true
      },
      {
        id: 'column-2',
        name: 'Column 2',
        position: { row: 2, col: 2 },
        suggestedComponents: ['card', 'image', 'text', 'button'],
        gridArea: 'col2',
        placeholder: 'Add second column',
        allowMultiple: true,
        maxComponents: 5,
        required: true
      },
      {
        id: 'column-3',
        name: 'Column 3',
        position: { row: 2, col: 3 },
        suggestedComponents: ['card', 'image', 'text', 'button'],
        gridArea: 'col3',
        placeholder: 'Add third column',
        allowMultiple: true,
        maxComponents: 5,
        required: true
      },
      {
        id: 'column-4',
        name: 'Column 4',
        position: { row: 2, col: 4 },
        suggestedComponents: ['card', 'image', 'text', 'button'],
        gridArea: 'col4',
        placeholder: 'Add fourth column',
        allowMultiple: true,
        maxComponents: 5,
        required: true
      },
      {
        id: 'footer',
        name: 'Footer',
        position: { row: 3, col: '1/5' },
        suggestedComponents: ['text', 'paragraph', 'button'],
        gridArea: 'footer',
        placeholder: 'Add footer content',
        allowMultiple: true,
        maxComponents: 5
      }
    ]
  },

  // ============================================
  // HERO + CONTENT LAYOUT
  // ============================================
  { 
    id: 'hero-section', 
    name: 'Hero + Content',
    description: 'Full-width hero section with content area below - ideal for landing pages',
    preview: '/assets/layout-icons/hero-content.png',
    gridClass: 'hero-content-grid',
    type: 'slot-based',
    layoutSpacing: {
      desktop: { gap: 32, padding: 20 },
      tablet: { gap: 24, padding: 16 },
      mobile: { gap: 16, padding: 12 }
    },
    slots: [
      {
        id: 'hero',
        name: 'Hero Section',
        position: { row: 1, col: 1 },
        suggestedComponents: ['image', 'carousel', 'header', 'card', 'video'],
        gridArea: 'hero',
        placeholder: 'Add your hero content (large image, video, or carousel)',
        allowMultiple: false,
        maxComponents: 1,
        required: true
      },
      {
        id: 'content',
        name: 'Content Area',
        position: { row: 2, col: 1 },
        suggestedComponents: ['text', 'paragraph', 'card', 'image', 'form', 'list'],
        gridArea: 'content',
        placeholder: 'Add your main content',
        allowMultiple: true,
        maxComponents: 15,
        required: true
      },
      {
        id: 'footer',
        name: 'Footer',
        position: { row: 3, col: 1 },
        suggestedComponents: ['text', 'paragraph', 'button'],
        gridArea: 'footer',
        placeholder: 'Add footer content',
        allowMultiple: true,
        maxComponents: 5
      }
    ]
  },

  // ============================================
  // LEFT SIDEBAR LAYOUT
  // ============================================
  { 
    id: 'sidebar-left', 
    name: 'Left Sidebar',
    description: 'Left sidebar with main content area - perfect for navigation-heavy sites',
    preview: '/assets/layout-icons/sidebar-left-main.png',
    gridClass: 'sidebar-left-grid',
    type: 'slot-based',
    layoutSpacing: {
      desktop: { gap: 24, padding: 20 },
      tablet: { gap: 20, padding: 16 },
      mobile: { gap: 16, padding: 12 }
    },
    slots: [
      {
        id: 'header',
        name: 'Header',
        position: { row: 1, col: '1/3' },
        suggestedComponents: ['header', 'image', 'text'],
        gridArea: 'header',
        placeholder: 'Add header content',
        allowMultiple: true,
        maxComponents: 3
      },
      {
        id: 'sidebar',
        name: 'Left Sidebar',
        position: { row: 2, col: 1 },
        suggestedComponents: ['list', 'button', 'card', 'text'],
        gridArea: 'sidebar',
        placeholder: 'Add sidebar content (navigation, links, widgets)',
        allowMultiple: true,
        maxComponents: 10,
        required: true
      },
      {
        id: 'main-content',
        name: 'Main Content',
        position: { row: 2, col: 2 },
        suggestedComponents: ['text', 'paragraph', 'card', 'image', 'form', 'table'],
        gridArea: 'main',
        placeholder: 'Add your main content',
        allowMultiple: true,
        maxComponents: 15,
        required: true
      },
      {
        id: 'footer',
        name: 'Footer',
        position: { row: 3, col: '1/3' },
        suggestedComponents: ['text', 'paragraph', 'button'],
        gridArea: 'footer',
        placeholder: 'Add footer content',
        allowMultiple: true,
        maxComponents: 5
      }
    ]
  },

  // ============================================
  // RIGHT SIDEBAR LAYOUT
  // ============================================
  { 
    id: 'sidebar-right', 
    name: 'Right Sidebar',
    description: 'Main content with right sidebar - great for blogs with sidebars',
    preview: '/assets/layout-icons/sidebar-right-main.png',
    gridClass: 'sidebar-right-grid',
    type: 'slot-based',
    layoutSpacing: {
      desktop: { gap: 24, padding: 20 },
      tablet: { gap: 20, padding: 16 },
      mobile: { gap: 16, padding: 12 }
    },
    slots: [
      {
        id: 'header',
        name: 'Header',
        position: { row: 1, col: '1/3' },
        suggestedComponents: ['header', 'image', 'text'],
        gridArea: 'header',
        placeholder: 'Add header content',
        allowMultiple: true,
        maxComponents: 3
      },
      {
        id: 'main-content',
        name: 'Main Content',
        position: { row: 2, col: 1 },
        suggestedComponents: ['text', 'paragraph', 'card', 'image', 'form', 'table'],
        gridArea: 'main',
        placeholder: 'Add your main content',
        allowMultiple: true,
        maxComponents: 15,
        required: true
      },
      {
        id: 'sidebar',
        name: 'Right Sidebar',
        position: { row: 2, col: 2 },
        suggestedComponents: ['list', 'button', 'card', 'text'],
        gridArea: 'sidebar',
        placeholder: 'Add sidebar content (widgets, related content)',
        allowMultiple: true,
        maxComponents: 10,
        required: true
      },
      {
        id: 'footer',
        name: 'Footer',
        position: { row: 3, col: '1/3' },
        suggestedComponents: ['text', 'paragraph', 'button'],
        gridArea: 'footer',
        placeholder: 'Add footer content',
        allowMultiple: true,
        maxComponents: 5
      }
    ]
  },

  // ============================================
  // 12-GRID DETAILED LAYOUT
  // ============================================
  { 
    id: '12-grid-detailed', 
    name: '12-Grid Detailed',
    description: 'Complete 12-column grid showcase - demonstrates all possible column divisions',
    preview: '/assets/layout-icons/12-grid-detailed.png',
    gridClass: 'grid-12-detailed',
    type: 'slot-based',
    layoutSpacing: {
      desktop: { gap: 16, padding: 20 },
      tablet: { gap: 12, padding: 16 },
      mobile: { gap: 8, padding: 12 }
    },
    slots: [
      // Row 1: Full Width (12/12)
      {
        id: 'row1-col12',
        name: '12/12 Full Width',
        position: { row: 1, col: '1/13' },
        suggestedComponents: ['header', 'image', 'carousel', 'text'],
        gridArea: 'row1-full',
        placeholder: '12/12',
        allowMultiple: true,
        maxComponents: 5
      },
      // Row 2: Two Halves (6/12 + 6/12)
      {
        id: 'row2-col6-1',
        name: '6/12 Left',
        position: { row: 2, col: '1/7' },
        suggestedComponents: ['card', 'image', 'text', 'form'],
        gridArea: 'row2-col1',
        placeholder: '6/12',
        allowMultiple: true,
        maxComponents: 5
      },
      {
        id: 'row2-col6-2',
        name: '6/12 Right',
        position: { row: 2, col: '7/13' },
        suggestedComponents: ['card', 'image', 'text', 'form'],
        gridArea: 'row2-col2',
        placeholder: '6/12',
        allowMultiple: true,
        maxComponents: 5
      },
      // Row 3: Three Columns (4/12 + 4/12 + 4/12)
      {
        id: 'row3-col4-1',
        name: '4/12 Column 1',
        position: { row: 3, col: '1/5' },
        suggestedComponents: ['card', 'image', 'text', 'button'],
        gridArea: 'row3-col1',
        placeholder: '4/12',
        allowMultiple: true,
        maxComponents: 3
      },
      {
        id: 'row3-col4-2',
        name: '4/12 Column 2',
        position: { row: 3, col: '5/9' },
        suggestedComponents: ['card', 'image', 'text', 'button'],
        gridArea: 'row3-col2',
        placeholder: '4/12',
        allowMultiple: true,
        maxComponents: 3
      },
      {
        id: 'row3-col4-3',
        name: '4/12 Column 3',
        position: { row: 3, col: '9/13' },
        suggestedComponents: ['card', 'image', 'text', 'button'],
        gridArea: 'row3-col3',
        placeholder: '4/12',
        allowMultiple: true,
        maxComponents: 3
      },
      // Row 4: Four Columns (3/12 each)
      {
        id: 'row4-col3-1',
        name: '3/12 Column 1',
        position: { row: 4, col: '1/4' },
        suggestedComponents: ['card', 'image', 'text', 'button'],
        gridArea: 'row4-col1',
        placeholder: '3/12',
        allowMultiple: true,
        maxComponents: 2
      },
      {
        id: 'row4-col3-2',
        name: '3/12 Column 2',
        position: { row: 4, col: '4/7' },
        suggestedComponents: ['card', 'image', 'text', 'button'],
        gridArea: 'row4-col2',
        placeholder: '3/12',
        allowMultiple: true,
        maxComponents: 2
      },
      {
        id: 'row4-col3-3',
        name: '3/12 Column 3',
        position: { row: 4, col: '7/10' },
        suggestedComponents: ['card', 'image', 'text', 'button'],
        gridArea: 'row4-col3',
        placeholder: '3/12',
        allowMultiple: true,
        maxComponents: 2
      },
      {
        id: 'row4-col3-4',
        name: '3/12 Column 4',
        position: { row: 4, col: '10/13' },
        suggestedComponents: ['card', 'image', 'text', 'button'],
        gridArea: 'row4-col4',
        placeholder: '3/12',
        allowMultiple: true,
        maxComponents: 2
      },
      // Row 5: Six Columns (2/12 each)
      {
        id: 'row5-col2-1',
        name: '2/12 Column 1',
        position: { row: 5, col: '1/3' },
        suggestedComponents: ['card', 'image', 'text', 'button'],
        gridArea: 'row5-col1',
        placeholder: '2/12',
        allowMultiple: false,
        maxComponents: 1
      },
      {
        id: 'row5-col2-2',
        name: '2/12 Column 2',
        position: { row: 5, col: '3/5' },
        suggestedComponents: ['card', 'image', 'text', 'button'],
        gridArea: 'row5-col2',
        placeholder: '2/12',
        allowMultiple: false,
        maxComponents: 1
      },
      {
        id: 'row5-col2-3',
        name: '2/12 Column 3',
        position: { row: 5, col: '5/7' },
        suggestedComponents: ['card', 'image', 'text', 'button'],
        gridArea: 'row5-col3',
        placeholder: '2/12',
        allowMultiple: false,
        maxComponents: 1
      },
      {
        id: 'row5-col2-4',
        name: '2/12 Column 4',
        position: { row: 5, col: '7/9' },
        suggestedComponents: ['card', 'image', 'text', 'button'],
        gridArea: 'row5-col4',
        placeholder: '2/12',
        allowMultiple: false,
        maxComponents: 1
      },
      {
        id: 'row5-col2-5',
        name: '2/12 Column 5',
        position: { row: 5, col: '9/11' },
        suggestedComponents: ['card', 'image', 'text', 'button'],
        gridArea: 'row5-col5',
        placeholder: '2/12',
        allowMultiple: false,
        maxComponents: 1
      },
      {
        id: 'row5-col2-6',
        name: '2/12 Column 6',
        position: { row: 5, col: '11/13' },
        suggestedComponents: ['card', 'image', 'text', 'button'],
        gridArea: 'row5-col6',
        placeholder: '2/12',
        allowMultiple: false,
        maxComponents: 1
      },
      // Row 6: Twelve Columns (1/12 each)
      {
        id: 'row6-col1-1',
        name: '1/12 Column 1',
        position: { row: 6, col: 1 },
        suggestedComponents: ['image', 'text', 'button'],
        gridArea: 'row6-col1',
        placeholder: '1/12',
        allowMultiple: false,
        maxComponents: 1
      },
      {
        id: 'row6-col1-2',
        name: '1/12 Column 2',
        position: { row: 6, col: 2 },
        suggestedComponents: ['image', 'text', 'button'],
        gridArea: 'row6-col2',
        placeholder: '1/12',
        allowMultiple: false,
        maxComponents: 1
      },
      {
        id: 'row6-col1-3',
        name: '1/12 Column 3',
        position: { row: 6, col: 3 },
        suggestedComponents: ['image', 'text', 'button'],
        gridArea: 'row6-col3',
        placeholder: '1/12',
        allowMultiple: false,
        maxComponents: 1
      },
      {
        id: 'row6-col1-4',
        name: '1/12 Column 4',
        position: { row: 6, col: 4 },
        suggestedComponents: ['image', 'text', 'button'],
        gridArea: 'row6-col4',
        placeholder: '1/12',
        allowMultiple: false,
        maxComponents: 1
      },
      {
        id: 'row6-col1-5',
        name: '1/12 Column 5',
        position: { row: 6, col: 5 },
        suggestedComponents: ['image', 'text', 'button'],
        gridArea: 'row6-col5',
        placeholder: '1/12',
        allowMultiple: false,
        maxComponents: 1
      },
      {
        id: 'row6-col1-6',
        name: '1/12 Column 6',
        position: { row: 6, col: 6 },
        suggestedComponents: ['image', 'text', 'button'],
        gridArea: 'row6-col6',
        placeholder: '1/12',
        allowMultiple: false,
        maxComponents: 1
      },
      {
        id: 'row6-col1-7',
        name: '1/12 Column 7',
        position: { row: 6, col: 7 },
        suggestedComponents: ['image', 'text', 'button'],
        gridArea: 'row6-col7',
        placeholder: '1/12',
        allowMultiple: false,
        maxComponents: 1
      },
      {
        id: 'row6-col1-8',
        name: '1/12 Column 8',
        position: { row: 6, col: 8 },
        suggestedComponents: ['image', 'text', 'button'],
        gridArea: 'row6-col8',
        placeholder: '1/12',
        allowMultiple: false,
        maxComponents: 1
      },
      {
        id: 'row6-col1-9',
        name: '1/12 Column 9',
        position: { row: 6, col: 9 },
        suggestedComponents: ['image', 'text', 'button'],
        gridArea: 'row6-col9',
        placeholder: '1/12',
        allowMultiple: false,
        maxComponents: 1
      },
      {
        id: 'row6-col1-10',
        name: '1/12 Column 10',
        position: { row: 6, col: 10 },
        suggestedComponents: ['image', 'text', 'button'],
        gridArea: 'row6-col10',
        placeholder: '1/12',
        allowMultiple: false,
        maxComponents: 1
      },
      {
        id: 'row6-col1-11',
        name: '1/12 Column 11',
        position: { row: 6, col: 11 },
        suggestedComponents: ['image', 'text', 'button'],
        gridArea: 'row6-col11',
        placeholder: '1/12',
        allowMultiple: false,
        maxComponents: 1
      },
      {
        id: 'row6-col1-12',
        name: '1/12 Column 12',
        position: { row: 6, col: 12 },
        suggestedComponents: ['image', 'text', 'button'],
        gridArea: 'row6-col12',
        placeholder: '1/12',
        allowMultiple: false,
        maxComponents: 1
      }
    ]
  },

  // ============================================
  // ADVANCED SLOT-BASED LAYOUTS
  // ============================================

  /**
   * Layout 1: Header with Sidebar Content
   * Structure: Logo + Navigation in header, Sidebar on left, Main content area
   */
  {
    id: 'layout-1-header-sidebar',
    name: 'Header Sidebar',
    description: 'Left sidebar + main content',
    preview: '/assets/layout-icons/12-column-rush-grid.png',
    type: 'slot-based',
    gridClass: 'layout-1-grid', // Custom CSS class
    layoutSpacing: {
      desktop: { gap: 24, padding: 20 },
      tablet: { gap: 20, padding: 16 },
      mobile: { gap: 16, padding: 12 }
    },
    slots: [
      {
        id: 'logo',
        name: 'Logo Area',
        position: { row: 1, col: 1 },
        suggestedComponents: ['image', 'text', 'header'],
        gridArea: 'logo',
        placeholder: 'Add your logo here'
      },
      {
        id: 'navigation',
        name: 'Navigation',
        position: { row: 1, col: 2 },
        suggestedComponents: ['breadcrumb', 'button', 'list'],
        gridArea: 'navigation',
        placeholder: 'Add navigation menu'
      },
      {
        id: 'header-banner',
        name: 'Header/Banner',
        position: { row: 2, col: '1/3' },
        suggestedComponents: ['image', 'carousel', 'header', 'card'],
        gridArea: 'header',
        placeholder: 'Add header or banner content'
      },
      {
        id: 'sidebar',
        name: 'Side Bar',
        position: { row: 3, col: 1 },
        suggestedComponents: ['list', 'card', 'button', 'form'],
        gridArea: 'sidebar',
        placeholder: 'Add sidebar content'
      },
      {
        id: 'body',
        name: 'Body Area',
        position: { row: 3, col: 2 },
        suggestedComponents: ['text', 'paragraph', 'card', 'image', 'form', 'table'],
        gridArea: 'body',
        placeholder: 'Add main content here'
      },
      {
        id: 'footer',
        name: 'Footer',
        position: { row: 4, col: '1/3' },
        suggestedComponents: ['text', 'paragraph', 'button', 'list'],
        gridArea: 'footer',
        placeholder: 'Add footer content'
      }
    ],
    // Responsive breakpoints
    responsive: {
      mobile: 'layout-1-mobile',
      tablet: 'layout-1-tablet',
      desktop: 'layout-1-desktop'
    }
  },

  /**
   * Layout 2: Intro with Three Boxes
   * Structure: Logo + Navigation, Header, Intro text, Three content boxes, Footer
   */
  {
    id: 'layout-2-intro-boxes',
    name: 'Intro Three Boxes',
    description: 'Intro section + 3 content boxes',
    preview: '/assets/layout-icons/intro-three-boxes.png',
    type: 'slot-based',
    gridClass: 'layout-2-grid',
    layoutSpacing: {
      desktop: { gap: 28, padding: 20 },
      tablet: { gap: 24, padding: 16 },
      mobile: { gap: 16, padding: 12 }
    },
    slots: [
      {
        id: 'logo',
        name: 'Logo',
        position: { row: 1, col: 1 },
        suggestedComponents: ['image', 'text', 'header'],
        gridArea: 'logo',
        placeholder: 'Add logo'
      },
      {
        id: 'navigation',
        name: 'Navigation',
        position: { row: 1, col: 2 },
        suggestedComponents: ['breadcrumb', 'button', 'list'],
        gridArea: 'navigation',
        placeholder: 'Add navigation'
      },
      {
        id: 'header-banner',
        name: 'Header/Banner',
        position: { row: 2, col: '1/3' },
        suggestedComponents: ['image', 'carousel', 'header'],
        gridArea: 'header',
        placeholder: 'Add header banner'
      },
      {
        id: 'intro',
        name: 'Intro Text Area',
        position: { row: 3, col: '1/3' },
        suggestedComponents: ['text', 'paragraph', 'header', 'card'],
        gridArea: 'intro',
        placeholder: 'Add introduction text'
      },
      {
        id: 'box1',
        name: 'Box 1',
        position: { row: 4, col: 1 },
        suggestedComponents: ['card', 'image', 'text', 'button'],
        gridArea: 'box1',
        placeholder: 'Add first content box'
      },
      {
        id: 'box2',
        name: 'Box 2',
        position: { row: 4, col: 2 },
        suggestedComponents: ['card', 'image', 'text', 'button'],
        gridArea: 'box2',
        placeholder: 'Add second content box'
      },
      {
        id: 'box3',
        name: 'Box 3',
        position: { row: 4, col: 3 },
        suggestedComponents: ['card', 'image', 'text', 'button'],
        gridArea: 'box3',
        placeholder: 'Add third content box'
      },
      {
        id: 'footer',
        name: 'Footer',
        position: { row: 5, col: '1/4' },
        suggestedComponents: ['text', 'paragraph', 'button', 'list'],
        gridArea: 'footer',
        placeholder: 'Add footer'
      }
    ],
    responsive: {
      mobile: 'layout-2-mobile',
      tablet: 'layout-2-tablet',
      desktop: 'layout-2-desktop'
    }
  },

  /**
   * Layout 3: Hero Plus Grid
   * Structure: Full-width hero section with 3-column grid below
   */
  {
    id: 'layout-3-hero-grid',
    name: 'Hero + Grid',
    description: 'Hero section + 3-column grid below',
    preview: '/assets/layout-icons/three-equal-columns.png',
    type: 'slot-based',
    gridClass: 'layout-3-grid',
    layoutSpacing: {
      desktop: { gap: 32, padding: 20 },
      tablet: { gap: 24, padding: 16 },
      mobile: { gap: 16, padding: 12 }
    },
    slots: [
      {
        id: 'hero',
        name: 'Hero Section',
        position: { row: 1, col: '1/4' },
        suggestedComponents: ['image', 'carousel', 'header', 'card', 'video'],
        gridArea: 'hero',
        placeholder: 'Add hero content',
        maxComponents: 1
      },
      {
        id: 'grid-col-1',
        name: 'Column 1',
        position: { row: 2, col: 1 },
        suggestedComponents: ['card', 'image', 'text', 'button'],
        gridArea: 'col1',
        placeholder: 'Add first column content',
        allowMultiple: true,
        maxComponents: 3
      },
      {
        id: 'grid-col-2',
        name: 'Column 2',
        position: { row: 2, col: 2 },
        suggestedComponents: ['card', 'image', 'text', 'button'],
        gridArea: 'col2',
        placeholder: 'Add second column content',
        allowMultiple: true,
        maxComponents: 3
      },
      {
        id: 'grid-col-3',
        name: 'Column 3',
        position: { row: 2, col: 3 },
        suggestedComponents: ['card', 'image', 'text', 'button'],
        gridArea: 'col3',
        placeholder: 'Add third column content',
        allowMultiple: true,
        maxComponents: 3
      },
      {
        id: 'footer',
        name: 'Footer',
        position: { row: 3, col: '1/4' },
        suggestedComponents: ['text', 'paragraph', 'button', 'list'],
        gridArea: 'footer',
        placeholder: 'Add footer'
      }
    ],
    responsive: {
      mobile: 'layout-4-mobile',
      tablet: 'layout-4-tablet',
      desktop: 'layout-4-desktop'
    }
  },

  /**
   * Layout 4: Side Navigation with Content
   * Structure: Logo at top, Side navigation on left, Main content area
   */
  {
    id: 'layout-4-sidenav-content',
    name: 'Side Navigation',
    description: 'Side navigation with main content',
    preview: '/assets/layout-icons/left-sidebar-main.png',
    type: 'slot-based',
    gridClass: 'layout-4-grid',
    layoutSpacing: {
      desktop: { gap: 24, padding: 20 },
      tablet: { gap: 20, padding: 16 },
      mobile: { gap: 16, padding: 12 }
    },
    slots: [
      {
        id: 'logo',
        name: 'Logo',
        position: { row: 1, col: '1/3' },
        suggestedComponents: ['image', 'text', 'header'],
        gridArea: 'logo',
        placeholder: 'Add logo'
      },
      {
        id: 'side-navigation',
        name: 'Side bar Navigation',
        position: { row: 2, col: 1 },
        suggestedComponents: ['list', 'button', 'breadcrumb'],
        gridArea: 'sidenav',
        placeholder: 'Add side navigation'
      },
      {
        id: 'header-banner',
        name: 'Header/Banner',
        position: { row: 2, col: 2, rowSpan: 1 },
        suggestedComponents: ['image', 'carousel', 'header', 'card'],
        gridArea: 'header',
        placeholder: 'Add header content'
      },
      {
        id: 'body',
        name: 'Body Area',
        position: { row: 3, col: 2 },
        suggestedComponents: ['text', 'paragraph', 'card', 'image', 'form', 'table'],
        gridArea: 'body',
        placeholder: 'Add main content'
      },
      {
        id: 'footer',
        name: 'Footer',
        position: { row: 4, col: '1/3' },
        suggestedComponents: ['text', 'paragraph', 'button'],
        gridArea: 'footer',
        placeholder: 'Add footer'
      }
    ],
    responsive: {
      mobile: 'layout-3-mobile',
      tablet: 'layout-3-tablet',
      desktop: 'layout-3-desktop'
    }
  },

  /**
   * Layout 5: Grid Gallery with Boxes
   * Structure: Logo + Navigation, Grid of images/cards, Three bottom boxes, Footer
   */
  {
    id: 'layout-5-grid-gallery',
    name: 'Grid Gallery',
    description: 'Gallery grid with content boxes',
    preview: '/assets/layout-icons/hero-plus-grid.png',
    type: 'slot-based',
    gridClass: 'layout-5-grid',
    layoutSpacing: {
      desktop: { gap: 28, padding: 20 },
      tablet: { gap: 24, padding: 16 },
      mobile: { gap: 16, padding: 12 }
    },
    slots: [
      {
        id: 'logo',
        name: 'Logo',
        position: { row: 1, col: 1 },
        suggestedComponents: ['image', 'text', 'header'],
        gridArea: 'logo',
        placeholder: 'Add logo'
      },
      {
        id: 'navigation',
        name: 'Navigation',
        position: { row: 1, col: 2 },
        suggestedComponents: ['breadcrumb', 'button', 'list'],
        gridArea: 'navigation',
        placeholder: 'Add navigation'
      },
      {
        id: 'gallery',
        name: 'Gallery Grid',
        position: { row: 2, col: '1/3' },
        suggestedComponents: ['carousel', 'image', 'card', 'video'],
        gridArea: 'gallery',
        placeholder: 'Add gallery items (images, cards, videos)',
        allowMultiple: true, // This slot can contain multiple components
        maxComponents: 12 // Limit for grid display
      },
      {
        id: 'box1',
        name: 'Box 1',
        position: { row: 3, col: 1 },
        suggestedComponents: ['card', 'image', 'text', 'button'],
        gridArea: 'box1',
        placeholder: 'Add first box'
      },
      {
        id: 'box2',
        name: 'Box 2',
        position: { row: 3, col: 2 },
        suggestedComponents: ['card', 'image', 'text', 'button'],
        gridArea: 'box2',
        placeholder: 'Add second box'
      },
      {
        id: 'box3',
        name: 'Box 3',
        position: { row: 3, col: 3 },
        suggestedComponents: ['card', 'image', 'text', 'button'],
        gridArea: 'box3',
        placeholder: 'Add third box'
      },
      {
        id: 'footer',
        name: 'Footer',
        position: { row: 4, col: '1/4' },
        suggestedComponents: ['text', 'paragraph', 'button', 'list'],
        gridArea: 'footer',
        placeholder: 'Add footer'
      }
    ],
    responsive: {
      mobile: 'layout-5-mobile',
      tablet: 'layout-5-tablet',
      desktop: 'layout-5-desktop'
    }
  }
];

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Get layout by ID
 * @param {string} id - Layout ID
 * @returns {Object} Layout configuration
 */
export const getLayout = (id) => LAYOUTS.find(layout => layout.id === id) || LAYOUTS[0];

/**
 * Get all available layouts
 * @returns {Array} Array of all layouts
 */
export const getAvailableLayouts = () => LAYOUTS;

/**
 * Check if layout uses slot-based system (all layouts are now slot-based)
 * @param {string} layoutId - Layout ID
 * @returns {boolean} Always true
 */
export const isSlotBasedLayout = (layoutId) => {
  return true; // All layouts are now slot-based
};

/**
 * Get slots for a specific layout
 * @param {string} layoutId - Layout ID
 * @returns {Array} Array of slot definitions
 */
export const getLayoutSlots = (layoutId) => {
  const layout = getLayout(layoutId);
  return layout.slots || [];
};

/**
 * Get slot by ID within a layout
 * @param {string} layoutId - Layout ID
 * @param {string} slotId - Slot ID
 * @returns {Object|null} Slot definition or null if not found
 */
export const getSlotById = (layoutId, slotId) => {
  const slots = getLayoutSlots(layoutId);
  return slots.find(slot => slot.id === slotId) || null;
};

/**
 * Get suggested components for a slot
 * @param {string} layoutId - Layout ID
 * @param {string} slotId - Slot ID
 * @returns {Array} Array of suggested component types
 */
export const getSlotSuggestedComponents = (layoutId, slotId) => {
  const slot = getSlotById(layoutId, slotId);
  return slot?.suggestedComponents || [];
};

/**
 * Get required slots for a layout (slots that must have components)
 * @param {string} layoutId - Layout ID
 * @returns {Array} Array of required slot IDs
 */
export const getRequiredSlots = (layoutId) => {
  const slots = getLayoutSlots(layoutId);
  return slots.filter(slot => slot.required).map(slot => slot.id);
};

/**
 * Validate if all required slots have components
 * @param {string} layoutId - Layout ID
 * @param {Object} slotComponents - Object mapping slot IDs to component arrays
 * @returns {Object} { valid: boolean, emptySlots: Array }
 */
export const validateLayout = (layoutId, slotComponents) => {
  const requiredSlots = getRequiredSlots(layoutId);
  const emptySlots = requiredSlots.filter(
    slotId => !slotComponents[slotId] || slotComponents[slotId].length === 0
  );
  
  return {
    valid: emptySlots.length === 0,
    emptySlots,
    message: emptySlots.length > 0 
      ? `Please add content to: ${emptySlots.map(id => getSlotById(layoutId, id)?.name).join(', ')}`
      : 'All required slots have content'
  };
};

/**
 * Get layout spacing configuration
 * @param {string} layoutId - Layout ID
 * @returns {Object} Spacing configuration { desktop, tablet, mobile }
 */
export const getLayoutSpacing = (layoutId) => {
  const layout = getLayout(layoutId);
  return layout?.layoutSpacing || {
    desktop: { gap: 24, padding: 20 },
    tablet: { gap: 20, padding: 16 },
    mobile: { gap: 16, padding: 12 }
  };
};

/**
 * Get responsive layout spacing based on viewport
 * @param {string} layoutId - Layout ID
 * @param {string} viewport - 'desktop' | 'tablet' | 'mobile'
 * @returns {Object} Spacing config { gap, padding }
 */
export const getLayoutSpacingForViewport = (layoutId, viewport = 'desktop') => {
  const spacing = getLayoutSpacing(layoutId);
  return spacing[viewport] || spacing.desktop || { gap: 24, padding: 20 };
};

/**
 * Get layout gap for current viewport (auto-detect based on window width)
 * @param {string} layoutId - Layout ID
 * @returns {number} Gap value in pixels
 */
export const getResponsiveLayoutGap = (layoutId) => {
  const width = typeof window !== 'undefined' ? window.innerWidth : 1920;
  let viewport = 'desktop';
  
  if (width < 768) {
    viewport = 'mobile';
  } else if (width < 1024) {
    viewport = 'tablet';
  }
  
  const spacing = getLayoutSpacingForViewport(layoutId, viewport);
  return spacing.gap;
};

/**
 * Get layout padding for current viewport (auto-detect based on window width)
 * @param {string} layoutId - Layout ID
 * @returns {number} Padding value in pixels
 */
export const getResponsiveLayoutPadding = (layoutId) => {
  const width = typeof window !== 'undefined' ? window.innerWidth : 1920;
  let viewport = 'desktop';
  
  if (width < 768) {
    viewport = 'mobile';
  } else if (width < 1024) {
    viewport = 'tablet';
  }
  
  const spacing = getLayoutSpacingForViewport(layoutId, viewport);
  return spacing.padding;
};
